import { Component, Suspense, lazy, useState } from 'react'
import VsScene from './VsScene.jsx'

/**
 * The battle's and the reveal's scene (D-191, D-194): the 2.5D arena when it is
 * switched on and this device can draw it, and VsScene — exactly as before —
 * every other time. `cue` is what the arena acts out (a beat of the story, or
 * the result); VsScene has no use for it.
 *
 * The arena is `import()`ed only when it is on, so with the flag off not a byte
 * of it (or of three.js) is downloaded. While it loads, and if it ever fails —
 * no WebGL, a lost context, the chunk not reachable offline — the kid sees
 * VsScene, and keeps seeing it for the rest of the visit.
 *
 * Switched on by `features.arena_scene` in public/roster.json (data/features.json,
 * Jeff's switch), or for one visit by `?arena=1` in the address — the way to
 * look at it on the iPad before the flag is flipped. `?arena=0` forces it off.
 *
 * Even on, the arena waits for the art (D-196): a fight plays in it only when
 * both animals have all four poses drawn. Any other fight, and its reveal, is
 * VsScene with the animals' emoji, exactly as before, and nothing of the arena
 * is downloaded for it. Each illustration imported adds its pairs, with no
 * change here. `?arena=all` puts every fight in the arena, placeholders and
 * all, for looking at them.
 */

const Arena = lazy(() => import('../arena/Arena.jsx'))

/** Read once, at load: the app cleans the address later. */
const override = (() => {
  try {
    const v = new URLSearchParams(window.location.search).get('arena')
    return v === '1' ? true : v === '0' ? false : v === 'all' ? 'all' : null
  } catch {
    return null
  }
})()

/** The poses a battle shows (SPEC 6.2). */
const POSES = ['neutral', 'fierce', 'victorious', 'retreating']

/**
 * Whether an animal is drawn for the arena: every pose is real art that came
 * through the pipeline (`art.spec` in roster.json, D-187). An animal with some
 * poses drawn is not yet: its fight would change from art to placeholder and
 * back between beats.
 */
export function illustrated(animal) {
  return POSES.every((pose) => animal?.art?.spec?.includes(pose))
}

let failed = false

function webgl() {
  try {
    const c = document.createElement('canvas')
    return !!(c.getContext('webgl2') || c.getContext('webgl'))
  } catch {
    return false
  }
}

export function arenaWanted(features, a, b) {
  if (failed || !(override ?? features?.arena_scene === true)) return false
  if (override !== 'all' && !(illustrated(a) && illustrated(b))) return false
  return webgl()
}

/** A failed chunk load is an error thrown in render; this turns it into the fallback. */
class Fallback extends Component {
  constructor(props) { super(props); this.state = { broken: false } }
  static getDerivedStateFromError() { return { broken: true } }
  componentDidCatch(err) { console.error('arena: fell back to the CSS scene', err); failed = true; this.props.onFail?.() }
  render() { return this.state.broken ? this.props.fallback : this.props.children }
}

export default function ArenaSlot({ features, a, b, poseA, poseB, setting, badge, lungeA, lungeB, cue }) {
  const [, setFailedHere] = useState(false)
  const vs = <VsScene a={a} b={b} poseA={poseA} poseB={poseB} setting={setting} badge={badge} lungeA={lungeA} lungeB={lungeB} />
  if (!arenaWanted(features, a, b)) return vs
  const fail = () => { failed = true; setFailedHere(true) }
  return (
    <Fallback fallback={vs} onFail={fail}>
      <Suspense fallback={vs}>
        <Arena a={a} b={b} poseA={poseA} poseB={poseB} cue={cue} badge={badge} onFail={fail} />
      </Suspense>
    </Fallback>
  )
}
